/**
 * Shared UI components for Travel Demo
 * 
 * Reusable components for badges, cards, and displays used across
 * the wallet-centric travel demonstrator.
 */

import React from 'react';
import { Badge } from '../../ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Share2, 
  Shield, 
  Eye, 
  EyeOff,
  AlertTriangle,
  FileText,
  Receipt,
  ChevronRight
} from 'lucide-react';

// ============== DATA BOUNDARY NORMALIZATION ==============
// Ensures frontend receives arrays even if backend sends undefined/null

/**
 * Normalize actor access grant to ensure can_see/cannot_see are always arrays.
 * Handles both old field names (data_accessible/data_excluded) and new (can_see/cannot_see).
 */
const normalizeActorGrant = (grant) => ({
  ...grant,
  can_see: grant.can_see ?? grant.data_accessible ?? [],
  cannot_see: grant.cannot_see ?? grant.data_excluded ?? [],
  actor_role: grant.actor_role ?? grant.role ?? 'unknown',
  status: grant.status ?? 'active'
});

// ============== SHARED COMPONENTS ==============
// Provenance badge for journey credentials
export const ProvenanceBadge = ({ level }) => {
  const config = {
    you_provided: { label: 'You Provided', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    operator_issued: { label: 'Operator Issued', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    network_confirmed: { label: 'Network Confirmed', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    station_confirmed: { label: 'Station Confirmed', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
    carer_authorised: { label: 'Carer Authorised', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
  };
  const { label, color } = config[level] || { label: level, color: 'bg-slate-500/20 text-slate-400' };
  return (
    <Badge variant="outline" className={`text-xs ${color}`}>
      {label}
    </Badge>
  );
};

// Sharing status badge
export const SharingBadge = ({ status }) => {
  const config = {
    shared: { label: 'Shared', icon: Share2, color: 'text-emerald-400' },
    private: { label: 'Private', icon: EyeOff, color: 'text-slate-400' },
    bounded: { label: 'Bounded', icon: Eye, color: 'text-amber-400' }
  };
  const { label, icon: Icon, color } = config[status] || config.private;
  return (
    <span className={`flex items-center gap-1 text-xs ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Wallet credential card
export const WalletCredentialCard = ({ credential }) => (
  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors">
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-cyan-400" />
        <span className="font-medium text-white">{credential.title}</span>
      </div>
      <SharingBadge status={credential.sharing_status} />
    </div>
    <div className="text-sm text-slate-400 mb-2">{credential.description}</div>
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">Held in your wallet</span>
      <ProvenanceBadge level={credential.provenance} />
    </div>
    <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
      <Shield className="w-3 h-3" />
      <span>Custody: {credential.custody}</span>
    </div>
    {credential.shared_with && credential.shared_with.length > 0 && (
      <div className="mt-2 pt-2 border-t border-slate-700/50">
        <div className="flex items-center gap-1 text-xs text-cyan-400">
          <Share2 className="w-3 h-3" />
          <span>Shared with: {credential.shared_with.join(', ')}</span>
        </div>
      </div>
    )}
  </div>
);

// Actor access grant card
export const ActorAccessCard = ({ grant: rawGrant }) => {
  const grant = normalizeActorGrant(rawGrant);
  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            grant.status === 'active' ? 'bg-emerald-400' : 'bg-slate-500'
          }`} />
          <span className="font-medium text-white">{grant.actor_name}</span>
        </div>
        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
          {grant.actor_role}
        </Badge>
      </div>
      <div className="text-xs text-slate-500 mb-3">{grant.purpose}</div>
      <div className="space-y-2">
        <div>
          <div className="flex items-center gap-1 text-xs text-emerald-400 mb-1">
            <Eye className="w-3 h-3" />
            <span>May see:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {grant.can_see.map((item, i) => (
              <Badge key={`see-${i}`} className="text-xs bg-emerald-500/10 text-emerald-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1 text-xs text-rose-400 mb-1">
            <EyeOff className="w-3 h-3" />
            <span>Cannot see:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {grant.cannot_see.map((item, i) => (
              <Badge key={`nosee-${i}`} className="text-xs bg-rose-500/10 text-rose-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-slate-700/50 text-xs text-slate-500">
        <Shield className="w-3 h-3 inline mr-1" />
        {grant.authority_basis || grant.granted_by}
      </div>
    </div>
  );
};

// Receipt pack entry
export const ReceiptPackEntry = ({ entry, index }) => (
  <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs font-medium">
          {index + 1}
        </div>
        <span className="font-medium text-white text-sm">{entry.action}</span>
      </div>
      <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
        {entry.receipt_id}
      </Badge>
    </div>
    <div className="ml-8 space-y-1 text-xs">
      <div className="text-slate-400">
        <span className="text-slate-500">By:</span> {entry.actor}
      </div>
      {entry.data_shared && entry.data_shared.length > 0 && (
        <div className="flex items-start gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5" />
          <span className="text-emerald-400">{entry.data_shared.join(', ')}</span>
        </div>
      )}
      {entry.data_withheld && entry.data_withheld.length > 0 && (
        <div className="flex items-start gap-1">
          <XCircle className="w-3 h-3 text-rose-400 mt-0.5" />
          <span className="text-rose-400">Withheld: {entry.data_withheld.join(', ')}</span>
        </div>
      )}
      <div className="text-slate-500 font-mono text-xs truncate">
        Journey ref: {entry.episode_ref}
      </div>
    </div>
  </div>
);

// Message bubble style helper
export const getMessageBubbleStyle = (isUser, isDisruption) => {
  if (isUser) return 'bg-cyan-600 text-white rounded-br-sm';
  if (isDisruption) return 'bg-amber-500/20 text-amber-100 border border-amber-500/30 rounded-bl-sm';
  return 'bg-slate-800 text-white rounded-bl-sm';
};

// Message bubble
export const MessageBubble = ({ message }) => {
  const isUser = message.is_user;
  const isDisruption = message.content?.includes('Platform change') || message.content?.includes('⚠️');

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className={`rounded-2xl px-4 py-3 ${getMessageBubbleStyle(isUser, isDisruption)}`}>
          {!isUser && (
            <div className={`text-xs mb-1 ${isDisruption ? 'text-amber-400' : 'text-slate-400'}`}>
              {message.sender}
            </div>
          )}
          <div className="text-sm">{message.content}</div>
        </div>
        {message.governance_note && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <Shield className="w-3 h-3" />
            {message.governance_note}
          </div>
        )}
      </div>
    </div>
  );
};

// Disruption alert banner
export const DisruptionBanner = ({ notice }) => {
  if (!notice) return null;
  
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-medium text-amber-100">{notice.title}</div>
          <div className="text-sm text-amber-200/80 mt-1">{notice.message}</div>
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className="text-slate-400">
              Was: <span className="line-through text-slate-500">{notice.original_platform}</span>
            </span>
            <span className="text-amber-400">
              Now: {notice.new_platform} ({notice.access_note})
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Issued by {notice.issued_by}
          </div>
        </div>
      </div>
    </div>
  );
};
